import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EmployeeApi from '../employee/employeeApi';
import TimecardApi from '../timecard/timecardApi';
import { Employee } from '../employee/employeeType';
import { SendTimecardLinkRequest } from '../timecard/timecardType';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Mail, MessageSquare } from 'lucide-react';
import { formatDate } from '../lib/global_function';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';

const SendTimecardPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Company', path: '/main/company' },
    { label: 'Employee', path: '/main/company/employee' },
    { label: 'Send Timecard', path: '/main/company/employee/sendtimecard' },
  ];
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [sendMethod, setSendMethod] = useState<'email' | 'text'>('email');
  const [sending, setSending] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [preSelectedEmployee, setPreSelectedEmployee] = useState<Employee | null>(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    // Check if an employee was passed via navigation state
    if (location.state?.selectedEmployee) {
      const passedEmployee = location.state.selectedEmployee as Employee;
      setPreSelectedEmployee(passedEmployee);
      setSelectedEmployee(passedEmployee);
      setLoading(false);
      return;
    }

    // Only fetch all employees if no employee was pre-selected
    const fetchEmployees = async () => {
      try {
        const response = await EmployeeApi.get();
        setEmployees(response);
      } catch (err) {
        setError('Failed to load employees. Please try again.');
        //console.error('Error fetching employees:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [location.state]);

  const handleSelectEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setSuccess(null);
    setError(null);
  };

  const handleSendLink = async (method: 'email' | 'text') => {
    if (!selectedEmployee) {
      setError('Please select an employee first.');
      return;
    }

    setSendMethod(method);
    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const sendTimeCardRequestBody: SendTimecardLinkRequest = {
        employee_id: selectedEmployee.employee_id,
        recipient_first_name: selectedEmployee.first_name,
        recipient_last_name: selectedEmployee.last_name,
        employee_code: selectedEmployee.employee_code,
        send_method: method === 'text' ? 'sms' : method,
        recipient_email: selectedEmployee.email,
        recipient_phone: selectedEmployee.phone,
        message_for_type: 'timecard'
      };
      
      const response = await TimecardApi.sendTimecardLink(sendTimeCardRequestBody);
      
      setSuccess(`Timecard link successfully sent to ${selectedEmployee.first_name} ${selectedEmployee.last_name} via ${method === 'text' ? 'SMS' : 'email'}.`);
    } catch (err) {
      setError(`Failed to send timecard link. ${err instanceof Error ? err.message : 'Please try again.'}`);
      //console.error('Error sending timecard link:', err);
    } finally {
      setSending(false);
    }
  };

  const handleEmail = () => handleSendLink('email');
  const handleText = () => handleSendLink('text');

  if (loading) {
    return (
      <>
        <HorizontalMenu />
        <div className="container-safe py-6">
          <Breadcrumbs items={breadcrumbs} />
          <Spinner />
        </div>
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-bold text-foreground mt-6 mb-4">Send Timecard Link</h2>
        
        {error && (
          <Alert variant="error">
            {error}
          </Alert>
        )}
  
        {success ? (
          <div>
            <Alert variant="success" className="mb-4">
              {success}
            </Alert>
            <p className="text-base text-foreground">
              The employee should receive the timecard link shortly.
            </p>
          </div>
        ) : (
          <>
            {/* Only show employee selection table if no employee was pre-selected */}
            {!preSelectedEmployee && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Select an Employee</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-12 px-4 py-3"></th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Employee Id</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Email</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Phone</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Type</th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">Start Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {employees.length > 0 ? (
                        employees.map((employee) => (
                          <tr 
                            key={employee.employee_id}
                            className={`cursor-pointer hover:bg-gray-50 ${
                              selectedEmployee?.employee_id === employee.employee_id ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => handleSelectEmployee(employee)}
                          >
                            <td className="px-4 py-3 text-center">
                              <input 
                                type="radio" 
                                name="selectedEmployee"
                                checked={selectedEmployee?.employee_id === employee.employee_id}
                                onChange={() => handleSelectEmployee(employee)}
                                className="size-4 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 text-paragraph">
                              {employee.first_name} {employee.last_name}
                            </td>
                            <td className="px-4 py-3 text-paragraph">{employee.employee_code}</td>
                            <td className="px-4 py-3 text-paragraph">{employee.email || 'N/A'}</td>
                            <td className="px-4 py-3 text-paragraph">{employee.phone || 'N/A'}</td>
                            <td className="px-4 py-3 text-paragraph">{employee.employee_type}</td>
                            <td className="px-4 py-3 text-paragraph">
                              {employee.start_date ? formatDate(employee.start_date) : 'N/A'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-5 text-paragraph">
                            No employees found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
  
            {selectedEmployee && (
              <>
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card mb-3">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Selected Employee</h3>
                  <div className="space-y-2">
                    <p className="text-base text-foreground">
                      <strong className="font-semibold">Name:</strong> {selectedEmployee.first_name} {selectedEmployee.last_name}
                    </p>
                    <p className="text-base text-foreground">
                      <strong className="font-semibold">Employee Id:</strong> {selectedEmployee.employee_code}
                    </p>
                    <p className="text-base text-foreground">
                      <strong className="font-semibold">Email:</strong> {selectedEmployee.email || 'N/A'}
                    </p>
                    <p className="text-base text-foreground">
                      <strong className="font-semibold">Phone:</strong> {selectedEmployee.phone || 'N/A'}
                    </p>
                  </div>
                </div>
  
                <div className="mb-6">
                  <p className="text-base font-semibold text-foreground mb-2">
                    You can send the following URL link of the time card to the employee by Email or Text
                  </p>
                  <p className="text-base text-foreground">
                    <strong className="font-semibold">Timecard URL:</strong>{' '}
                    <span className="text-primary break-all">
                      {process.env.REACT_APP_SITE_HOST_URL}/time/{selectedEmployee.employee_code}
                    </span>
                  </p>
                </div>
  
                <div className="flex gap-2.5 mb-4">
                  <Button
                    onClick={handleEmail}
                    size="medium"
                    variant="default"
                    disabled={sending || !selectedEmployee.email}
                    className={sending || !selectedEmployee.email ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    <Mail className="size-5" />
                    Send by Email
                  </Button>
                  <Button
                    onClick={handleText}
                    size="medium"
                    variant="default"
                    disabled={sending || !selectedEmployee.phone}
                    className={sending || !selectedEmployee.phone ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    <MessageSquare className="size-5" />
                    Send by Text
                  </Button>
                </div>
                
                {!selectedEmployee.email && !selectedEmployee.phone && (
                  <Alert variant="error">
                    This employee doesn't have an email or phone number on file. Please update their contact information.
                  </Alert>
                )}
                {!selectedEmployee.email && selectedEmployee.phone && (
                  <Alert variant="warning">
                    This employee doesn't have an email on file. Email option is disabled.
                  </Alert>
                )}
                {!selectedEmployee.phone && selectedEmployee.email && (
                  <Alert variant="warning">
                    This employee doesn't have a phone number on file. Text option is disabled.
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        <div className="mb-[150px]"></div>
      </div>
    </>
  );
};

export default SendTimecardPage;